// Notificação local de pausa (Etapa 12 — spec S03 §Cronômetro): "iniciar
// agenda notificação de pausa para +1h, repetindo a cada 1h de sessão
// contínua; pausar cancela". Configurável via preferências desde a Etapa 14
// (src/features/preferences/preferencesStore.ts) — desligada, iniciar o
// cronômetro simplesmente não agenda nada.
//
// Import de módulo nativo (expo-notifications) — sem teste automatizado,
// mesmo raciocínio de screenEnergy.ts/ProjectPhotosSection.tsx.
import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18next from '../../i18n';
import { getPreferences } from '../preferences/preferencesStore';

/** Intervalo de repetição do lembrete de pausa (spec S03: "+1h", repetindo
 * a cada 1h de sessão contínua). */
const PAUSE_REMINDER_INTERVAL_SECONDS = 60 * 60;

/** Identificador da notificação agendada, persistido (não em memória): o
 * app pode ser morto entre `schedulePauseReminder` e `cancelPauseReminder`
 * (iniciar sessão → kill → reabrir → pausar), então uma variável JS não
 * sobreviveria até o cancelamento. Guardar o id devolvido por
 * `scheduleNotificationAsync` é o que permite cancelar SÓ esta notificação
 * — ver o histórico desta função: antes usava
 * `cancelAllScheduledNotificationsAsync`, o que cancelaria também o
 * lembrete diário de "projeto atual" (dailyReminder.ts, Etapa 14) se ambos
 * estivessem agendados ao mesmo tempo. */
const PAUSE_REMINDER_ID_KEY = 'crochet:pause-reminder-notification-id';

/**
 * Agenda o lembrete de pausa. Sem efeito se a preferência estiver desligada
 * (Etapa 14). Pede permissão se ainda não concedida — no iOS, uma
 * notificação agendada sem permissão simplesmente nunca aparece,
 * silenciosamente, então pedimos aqui em vez de assumir que já foi
 * concedida em algum outro fluxo. Falha (permissão negada, ou qualquer erro
 * do módulo nativo) é tratada como "sem lembrete desta vez" — nunca deve
 * impedir a usuária de iniciar o cronômetro.
 */
export async function schedulePauseReminder(): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    const preferences = await getPreferences();
    if (!preferences.pauseReminderEnabled) return;

    const current = await Notifications.getPermissionsAsync();
    const granted = current.granted || (await Notifications.requestPermissionsAsync()).granted;
    if (!granted) return;

    const id = await Notifications.scheduleNotificationAsync({
      content: {
        title: i18next.t('session.pauseReminder.title'),
        body: i18next.t('session.pauseReminder.body'),
      },
      trigger: { seconds: PAUSE_REMINDER_INTERVAL_SECONDS, repeats: true },
    });
    await AsyncStorage.setItem(PAUSE_REMINDER_ID_KEY, id);
  } catch (error) {
    console.error('pauseNotification: falha ao agendar lembrete de pausa', error);
  }
}

/**
 * Cancela o lembrete de pausa ao pausar — só a notificação identificada por
 * `PAUSE_REMINDER_ID_KEY`, nunca as demais notificações agendadas do app.
 * Sem-op silencioso se nada estava agendado (preferência desligada, ou
 * `schedulePauseReminder` não chegou a agendar por falta de permissão).
 */
export async function cancelPauseReminder(): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    const id = await AsyncStorage.getItem(PAUSE_REMINDER_ID_KEY);
    if (!id) return;
    await Notifications.cancelScheduledNotificationAsync(id);
    await AsyncStorage.removeItem(PAUSE_REMINDER_ID_KEY);
  } catch (error) {
    console.error('pauseNotification: falha ao cancelar lembrete de pausa', error);
  }
}
